<template>
  <div>
    <div class="page-header bordered">
      <h1>Organizer Profile</h1>
    </div>

    <p>
      Please provide as much information about your organization for your attendees.
      Any information you provide will appear on your event and gift card listings.
    </p>

    <form>
      <ShowErrors
        :errorMessages="errorMessages"
      />

      <h3 class="subheadline">About you</h3>
      <p>
        Tell attendees about you as the host of your events
      </p>

      <div class="form-group">
        <label for="organizer_name" class="required">Organizer Name</label>
        <input type="text" class="form-control form-control-lg" name="organizer_name"
          v-model="organizerProfile.organizer_name" data-vv-as="Organizer name"
          autofocus placeholder="e.g., Acme Comedy" v-validate="'required|max:250'" />
        <span v-show="errors.has('organizer_name')" class="input-error">
          {{ errors.first('organizer_name') }}
        </span>
      </div>

      <div class="form-group">
        <label for="contact_email" class="required">Contact Email</label>
        <input type="text" class="form-control form-control-lg" name="contact_email"
          v-model="organizerProfile.contact_email" data-vv-as="Organizer name"
          v-validate="'email|required'" placeholder="e.g., help@acmecomedy.com" />
        <span v-show="errors.has('contact_email')" class="input-error">
          {{ errors.first('contact_email') }}
        </span>
      </div>

      <div class="form-group">
        <label for="contact_phone_number">Contact Phone Number</label>
        <input type="text" v-model="organizerProfile.contact_phone_number"
          name="contact_phone_number" class="form-control form-control-lg"
          placeholder="e.g., (555) 555-5555" />
      </div>

      <div class="form-group">
        <label for="organizer_website">Organizer website</label>
        <input type="text" class="form-control form-control-lg" name="organizer_website"
          v-model="organizerProfile.organizer_website" data-vv-as="Organizer name"
          v-validate="{ url: { require_protocol: true }}" placeholder="e.g., https://acmecomedy.com" />
        <span v-show="errors.has('organizer_website')" class="input-error">
          {{ errors.first('organizer_website') }}
        </span>
      </div>

      <div class="form-group">
        <label for="organizer_bio">Organizer Bio</label>
        <textarea class="form-control form-control-lg" name="organizer_bio"
          v-model="organizerProfile.organizer_bio" data-vv-as="Organizer bio"
          rows="4" placeholder="Write a short description of who you are, the types of events you host, or your mission. This will appear on all your event and gift card listings."
          v-validate="'max:250'"  />
        <span v-show="errors.has('organizer_bio')" class="input-error">
          {{ errors.first('organizer_bio') }}
        </span>
      </div>

      <h3 class="subheadline">Default time & place</h3>
      <p>
        If your events usually happen at a particular time or place, provide these details,
        and the information will automatically populate any new events you create.
      </p>

      <template>
        <div class="form-group">
          <label for="venue_name">Venue Name</label>
          <input type="text" class="form-control form-control-lg" name="venue_name"
            v-model="organizerProfile.venue_name" placeholder="e.g., Anvil Bar & Restaurant"
            data-vv-as="Venue name" />
          <span v-show="errors.has('venue_name')" class="input-error">
            {{ errors.first('venue_name') }}
          </span>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <div class="form-group">
              <label for="address_1">Address 1</label>
              <input type="text" class="form-control form-control-lg" name="address_1"
                data-vv-as="Address 1" placeholder="e.g., 1000 Warner St"
                v-validate="{ max: 250 }"
                v-model="organizerProfile.address_1">
              <span v-show="errors.has('address_1')" class="input-error">
                {{ errors.first('address_1') }}
              </span>
            </div>
          </div>

          <div class="col-lg-6">
            <div class="form-group">
              <label for="address_2">Address 2</label>
              <input type="text" class="form-control form-control-lg" name="address_2"
                data-vv-as="Address 2" placeholder="e.g., Ste 5A" v-model="organizerProfile.address_2"
                v-validate="'max:250'">
              <span v-show="errors.has('address_2')" class="input-error">
                {{ errors.first('address_2') }}
              </span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <div class="form-group">
              <label for="city" class="required">City</label>
              <input type="text" class="form-control form-control-lg" name="city"
                data-vv-as="City" placeholder="e.g., New York" v-model="organizerProfile.city"
                v-validate="{required: true, max: 250 }">
              <span v-show="errors.has('city')" class="input-error">
                {{ errors.first('city') }}
              </span>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="form-group">
              <label for="state" class="required">State</label>
              <select name="state" class="form-control form-control-lg ui-select"
                v-model="organizerProfile.state"
                v-validate="{ required: true }"
                data-vv-as="State">
                <option value=""> State </option>
                <option v-for="(state, index) in stateList" :key="index"
                  :value="state.value">
                  {{ state.name }}
                </option>
              </select>
              <span v-show="errors.has('state')" class="input-error">
                {{ errors.first('state') }}
              </span>
            </div>
          </div>
        </div>

        <div class="row">
          <div class="col-lg-6">
            <div class="form-group">
              <label for="zip_code" class="required">Zip Code</label>
              <input type="text" class="form-control form-control-lg" name="zip_code"
                data-vv-as="Zip Code" placeholder="e.g., 10001" v-model="organizerProfile.zip_code"
                v-validate="{ required: true, max: 15 }">
              <span v-show="errors.has('zip_code')" class="input-error">
                {{ errors.first('zip_code') }}
              </span>
            </div>
          </div>
        </div>
      </template>

      <div class="form-group">
        <label for="time_zone">Time zone</label>
        <select name="time_zone" class="form-control form-control-lg ui-select"
          v-model="organizerProfile.time_zone" data-vv-as="Time zone">
          <option value=""> Select time zone... </option>
          <option v-for="(timeZone, index) in timeZoneList" :key="index"
            :value="timeZone.value">
            {{ timeZone.name }}
          </option>
        </select>
        <span v-show="errors.has('time_zone')" class="input-error">
          {{ errors.first('time_zone') }}
        </span>
      </div>

      <h3 class="subheadline">Social media and marketing</h3>
      <p>
        Tell attendees how to find out more about you
      </p>

      <div class="form-group">
        <label for="facebook_id">Facebook ID</label>
        <input type="text" class="form-control form-control-lg" name="facebook_id"
          v-model="organizerProfile.facebook_id" data-vv-as="Organizer name"
          autofocus placeholder="e.g., acmecomedy" v-validate="'max:250'" />
        <span v-show="errors.has('facebook_id')" class="input-error">
          {{ errors.first('facebook_id') }}
        </span>
      </div>

      <div class="form-group">
        <label for="twitter_id">Twitter</label>
        <b-input-group size="lg" prepend="@">
          <b-form-input name="twitter_id"
          v-model="organizerProfile.twitter_id" data-vv-as="Twitter"
          autofocus placeholder="e.g., acmecomedy" v-validate="'max:250'"/>
        </b-input-group>
        <span v-show="errors.has('twitter_id')" class="input-error">
          {{ errors.first('twitter_id') }}
        </span>
      </div>

      <div class="form-group">
        <label for="instagram_id">Instagram </label>
        <b-input-group size="lg" prepend="@">
          <b-form-input name="instagram_id"
          v-model="organizerProfile.instagram_id" data-vv-as="Instagram"
          autofocus placeholder="e.g., acmecomedy" v-validate="'max:250'"/>
        </b-input-group>
        <span v-show="errors.has('instagram_id')" class="input-error">
          {{ errors.first('instagram_id') }}
        </span>
      </div>

      <div class="form-group">
        <label for="social_image_url">Social media image URL</label>
        <input type="text" class="form-control form-control-lg" name="social_image_url"
          v-model="organizerProfile.social_image_url" data-vv-as="Social media image URL"
          v-validate="{ url: { require_protocol: true }}" />
        <span v-show="errors.has('social_image_url')" class="input-error">
          {{ errors.first('social_image_url') }}
        </span>
        <p>
          Link to an image that is at least 1080px wide for best results on high-res devices. Square images will have
          better performance with image link Facebook ads.
        </p>
      </div>

      <div class="form-group">
        <b-form-checkbox switch size="lg" v-model="organizerProfile.email_opt_in"
          vid="enabled">
          Enable email opt-in
        </b-form-checkbox>
        Choose whether attendees can opt in to receiving marketing emails when they purchase tickets or gift cards.
      </div>

      <br />
      <hr />
      <div class="form-group action">
        <button type="button" class="btn btn-lg btn-primary" v-on:click="validate()">
          Save
        </button>
      </div>
    </form>
  </div>
</template>

<script>
import ShowErrors from '../common/ShowErrors'
import OrganizerProfileService from '../../services/OrganizerProfileService'

export default {
  name: 'OrganizerProfile',
  components: {
    ShowErrors
  },
  data () {
    return {
      timeZoneList: this.$timeZoneList,
      stateList: this.$stateList,
      organizerProfile: {},
      errorMessages: []
    }
  },
  created () {
    this.loadOrganizerProfile()
  },
  methods: {
    loadOrganizerProfile () {
      var self = this
      var loader = this.$loading.show()

      OrganizerProfileService.getOrganizerProfile().then(function (response) {
        self.organizerProfile = response.data
      }).finally(function () {
        loader.hide()
      })
    },
    validate () {
      var self = this

      this.$validator.validateAll().then((result) => {
        if (result) {
          self.updateOrganizerProfile()
        } else {
          self.$swal({
            title: 'Invalid organizer profile data',
            text: 'Please, check errors in form',
            type: 'error'
          })
        }
      })
    },
    updateOrganizerProfile () {
      this.errorMessages = []
      var self = this
      var loader = this.$loading.show()

      OrganizerProfileService.updateOrganizerProfile(this.organizerProfile)
        .then(function (response) {
          self.organizerProfile = response.data

          self.$swal({
            title: 'Organizer profile saved!',
            type: 'success'
          })
        }).catch(function (error) {
          if (error) {
            var errorMsg = error.response.data.message || error.response.data.error
            self.errorMessages = errorMsg

            self.$swal({
              title: 'Error updating Organizer profile',
              text: 'Please, check errors in form',
              type: 'error'
            })
          }
        }).finally(function () {
          loader.hide()
        })
    }
  }
}
</script>
